import { useCallback } from 'react'
import { DiscussionRepository } from '../../applications/ports/discussionRepository'
import api from '../../lib/commons/api'
import {
  Discussion,
  DiscussionComment,
  DiscussionTarget,
  DiscussionThread,
  TargetThread,
  VersionedDiscussion,
} from '../../domain/entity/DiscussionEntity'
import { dateTimeV2Service } from '../../domain/value-object/DateTimeV2'

type RawThread = {
  threadUuid: string
  discussionComments?: DiscussionComment[]
  targets?: DiscussionTarget[]
  createdAt: number
}

const mapThread = (raw: RawThread): DiscussionThread => ({
  threadUuid: raw.threadUuid,
  discussionComments: Array.isArray(raw.discussionComments)
    ? raw.discussionComments
    : [],
  targets: Array.isArray(raw.targets) ? raw.targets : [],
  createdAt: raw.createdAt,
})

const mapDiscussion = (data: {
  discussionUuid?: string
  discussionThreads?: RawThread[]
}): Discussion => ({
  discussionUuid: data.discussionUuid || '',
  discussionThreads: Array.isArray(data.discussionThreads)
    ? data.discussionThreads.map(mapThread)
    : [],
})

type RawTargetThread = {
  threadUuid: string
  versionUuid: string
  versionName: string
  versionPageUuid: string
  createdAt: number
  targets?: DiscussionTarget[]
  discussionComments?: DiscussionComment[]
}

const mapTargetThread = (raw: RawTargetThread): TargetThread => ({
  threadUuid: raw.threadUuid,
  versionUuid: raw.versionUuid,
  versionName: raw.versionName,
  versionPageUuid: raw.versionPageUuid,
  createdAt: raw.createdAt,
  targets: Array.isArray(raw.targets) ? raw.targets : [],
  discussionComments: Array.isArray(raw.discussionComments)
    ? raw.discussionComments
    : [],
})

export const useDiscussionRepository = (): DiscussionRepository => {
  const createThread = useCallback(
    async (
      versionReportPageUuid: string,
      content: string,
      discussionUuid?: string,
      media?: string,
      targetUuids?: string[]
    ) => {
      await api.functional.request(
        'POST',
        '/api/v1/projects/discussion/create/thread',
        {
          versionReportPageUuid,
          content,
          discussionUuid,
          media,
          ...(targetUuids && targetUuids.length > 0 ? { targetUuids } : {}),
        }
      )
    },
    []
  )

  const createReply = useCallback(
    async (threadUuid: string, content: string, media?: string) => {
      await api.functional.request(
        'POST',
        '/api/v1/projects/discussion/reply/thread',
        {
          threadUuid,
          content,
          media,
        }
      )
    },
    []
  )

  const getDiscussion = useCallback(
    async (versionReportPageUuid: string): Promise<Discussion> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/discussion/threads',
        { versionReportPageUuid }
      )
      return mapDiscussion(response.json)
    },
    []
  )

  const getDiscussionByVersion = useCallback(
    async (
      versionUuid: string,
      templatePageUuid: string
    ): Promise<VersionedDiscussion | null> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/discussion/threads/by-version',
        { versionUuid, templatePageUuid }
      )
      const data = response.json
      if (!data) {
        return null
      }
      return {
        versionUuid: data.versionUuid,
        versionName: data.versionName,
        takenAt: dateTimeV2Service.newFromString(data.takenAt),
        discussion: mapDiscussion(data.discussion ?? {}),
      }
    },
    []
  )

  const getThreadsByTarget = useCallback(
    async (
      targetUuid: string,
      currentVersionUuid: string
    ): Promise<TargetThread[]> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/projects/discussion/threads/by-target',
        { targetUuid, currentVersionUuid }
      )
      const data = response.json
      if (!data || !Array.isArray(data.threads)) {
        return []
      }
      return data.threads.map(mapTargetThread)
    },
    []
  )

  return {
    createThread,
    createReply,
    getDiscussion,
    getDiscussionByVersion,
    getThreadsByTarget,
  }
}
